/**
 * Cart state, shared across the app.
 *
 * The cart lives in WooCommerce, not in the browser — it is the only thing that
 * can price it correctly (tax is inclusive here, and coupons and shipping are
 * server rules). This module is a thin reactive mirror of the Store API
 * response, so components re-render when it changes.
 *
 * Loading is lazy and deduplicated: the header badge and the cart page both ask
 * for the cart, and only one request goes out.
 */

import { getCart, addItem, updateItem, removeItem, type Cart } from './store-api';

class CartState {
	cart = $state<Cart | null>(null);
	loading = $state(false);
	/** Last operation's failure, surfaced to the user rather than swallowed. */
	error = $state<string | null>(null);

	/** In-flight load, so concurrent callers share one request. */
	#pending: Promise<void> | null = null;

	get count(): number {
		return this.cart?.items_count ?? 0;
	}

	get isEmpty(): boolean {
		return !this.cart || this.cart.items.length === 0;
	}

	/**
	 * Fetches the cart. **Never rejects** — a failure lands in `this.error`.
	 *
	 * That asymmetry with `add`/`update`/`remove` is deliberate. Those are things
	 * the visitor just did, and a caller has to be able to react to them failing.
	 * This is a read that three components fire from `onMount` purely to populate
	 * themselves, and all three already render `error`; rejecting as well meant
	 * every one of them was an unhandled rejection.
	 *
	 * It was not hypothetical. With the WordPress origin unreachable the Store
	 * API answered 530, and the header's cart badge — mounted on every page of a
	 * site whose pages are otherwise fully static — logged an uncaught "Request
	 * failed (530)" into the console of every single page load.
	 */
	async load(force = false): Promise<void> {
		if (this.cart && !force) return;
		if (this.#pending) return this.#pending;

		this.#pending = this.#run(() => getCart()).catch(() => {
			// Swallowed, not ignored: `#run` has already recorded it in `error`,
			// which is what the cart page and checkout display.
		});
		try {
			await this.#pending;
		} finally {
			this.#pending = null;
		}
	}

	add(id: number, quantity = 1) {
		return this.#run(() => addItem(id, quantity));
	}

	update(key: string, quantity: number) {
		return this.#run(() => updateItem(key, quantity));
	}

	remove(key: string) {
		return this.#run(() => removeItem(key));
	}

	/** Applies an operation, keeping `loading`/`error` consistent. */
	async #run(operation: () => Promise<Cart>): Promise<void> {
		this.loading = true;
		this.error = null;
		try {
			this.cart = await operation();
		} catch (cause) {
			this.error = cause instanceof Error ? cause.message : 'Something went wrong';
			throw cause;
		} finally {
			this.loading = false;
		}
	}
}

export const cart = new CartState();
